import React, { useState, useEffect } from 'react';
import { 
  UserCircleIcon, 
  PencilIcon, 
  CheckIcon, 
  XMarkIcon, 
  EyeIcon, 
  EyeSlashIcon,
  KeyIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import { User } from '../../types';
import apiService from '../../services/api';

interface ProfileFormData {
  full_name: string;
  email: string;
  phone: string;
}

interface PasswordFormData {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

interface FormErrors {
  [key: string]: string;
}

const ProfilePage: React.FC = () => {
  const { state: authState, updateUser } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  
  const [profileData, setProfileData] = useState<ProfileFormData>({
    full_name: '',
    email: '',
    phone: ''
  });
  
  const [passwordData, setPasswordData] = useState<PasswordFormData>({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  
  const [errors, setErrors] = useState<FormErrors>({});

  useEffect(() => {
    if (authState.user) {
      setProfileData({
        full_name: authState.user.full_name || '',
        email: authState.user.email || '',
        phone: authState.user.phone || ''
      });
    }
  }, [authState.user]);

  const handleSaveProfile = async () => {
    setLoading(true);
    try {
      const updatedUser: User = {
        ...authState.user!,
        ...profileData
      };
      updateUser(updatedUser);
      setIsEditing(false);
      setSuccessMessage('Profile updated successfully!');
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (err: any) {
      setErrors({ submit: err.message || 'Failed to update profile' });
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    setLoading(true);
    try {
      await apiService.changePassword({
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setIsChangingPassword(false);
      setSuccessMessage('Password changed successfully!');
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (err: any) {
      setErrors({ password: err.message || 'Failed to change password' });
    } finally {
      setLoading(false);
    }
  };

  if (!authState.user) {
    return (
      <div className="space-y-6">
        <div className="card p-6">
          <p className="text-center text-gray-500">Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="border-b border-gray-200 pb-4">
        <h1 className="text-3xl font-bold text-gray-900">Profile</h1>
        <p className="mt-2 text-gray-600">Manage your account settings and personal information</p>
      </div>

      {successMessage && (
        <div className="bg-success-50 border border-success-200 rounded-md p-4">
          <div className="flex items-center">
            <CheckIcon className="h-5 w-5 text-success-600 mr-2" />
            <span className="text-success-700">{successMessage}</span>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="card p-6">
          <div className="text-center">
            <div className="mx-auto h-24 w-24 bg-primary-100 rounded-full flex items-center justify-center">
              <UserCircleIcon className="h-16 w-16 text-primary-600" />
            </div>
            <h3 className="mt-4 text-lg font-medium text-gray-900">
              {authState.user.full_name || authState.user.username}
            </h3>
            <p className="text-sm text-gray-500">{authState.user.email}</p>
            <div className="mt-4">
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                authState.user.role === 'Admin' 
                  ? 'bg-purple-100 text-purple-800' 
                  : 'bg-blue-100 text-blue-800'
              }`}>
                {authState.user.role}
              </span>
            </div>
          </div>
        </div>

        <div className="lg:col-span-2 space-y-6">
          <div className="card p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Personal Information</h3>
              {!isEditing && (
                <button onClick={() => setIsEditing(true)} className="btn-outline">
                  <PencilIcon className="h-4 w-4 mr-2" />
                  Edit
                </button>
              )}
            </div>

            <div className="space-y-4">
              <div>
                <label className="form-label">Full Name</label>
                <input
                  type="text"
                  name="full_name"
                  value={profileData.full_name}
                  onChange={(e) => setProfileData(prev => ({ ...prev, full_name: e.target.value }))}
                  disabled={!isEditing}
                  className={`form-input ${!isEditing ? 'bg-gray-50' : ''}`}
                />
              </div>

              <div>
                <label className="form-label">Email</label>
                <input
                  type="email"
                  name="email"
                  value={profileData.email}
                  onChange={(e) => setProfileData(prev => ({ ...prev, email: e.target.value }))}
                  disabled={!isEditing}
                  className={`form-input ${!isEditing ? 'bg-gray-50' : ''}`}
                />
              </div>

              <div>
                <label className="form-label">Phone</label>
                <input
                  type="tel"
                  name="phone"
                  value={profileData.phone}
                  onChange={(e) => setProfileData(prev => ({ ...prev, phone: e.target.value }))}
                  disabled={!isEditing}
                  className={`form-input ${!isEditing ? 'bg-gray-50' : ''}`}
                />
              </div>
            </div>

            {isEditing && (
              <div className="flex items-center justify-end space-x-3 mt-6 pt-4 border-t border-gray-200">
                <button onClick={() => setIsEditing(false)} className="btn-outline">
                  Cancel
                </button>
                <button onClick={handleSaveProfile} disabled={loading} className="btn-primary">
                  {loading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            )}
          </div>

          <div className="card p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Change Password</h3>
              {!isChangingPassword && (
                <button onClick={() => setIsChangingPassword(true)} className="btn-outline">
                  <KeyIcon className="h-4 w-4 mr-2" />
                  Change Password
                </button>
              )}
            </div>

            {isChangingPassword ? (
              <div className="space-y-4">
                <div>
                  <label className="form-label">Current Password</label>
                  <div className="relative">
                    <input
                      type={showCurrentPassword ? 'text' : 'password'}
                      value={passwordData.currentPassword}
                      onChange={(e) => setPasswordData(prev => ({ ...prev, currentPassword: e.target.value }))}
                      className="form-input pr-10"
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    >
                      {showCurrentPassword ? (
                        <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                      ) : (
                        <EyeIcon className="h-5 w-5 text-gray-400" />
                      )}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="form-label">New Password</label>
                  <div className="relative">
                    <input
                      type={showNewPassword ? 'text' : 'password'}
                      value={passwordData.newPassword}
                      onChange={(e) => setPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
                      className="form-input pr-10"
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                    >
                      {showNewPassword ? (
                        <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                      ) : (
                        <EyeIcon className="h-5 w-5 text-gray-400" />
                      )}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="form-label">Confirm New Password</label>
                  <input
                    type="password"
                    value={passwordData.confirmPassword}
                    onChange={(e) => setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                    className="form-input"
                  />
                </div>

                <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
                  <button onClick={() => setIsChangingPassword(false)} className="btn-outline">
                    Cancel
                  </button>
                  <button onClick={handleChangePassword} disabled={loading} className="btn-primary">
                    {loading ? 'Changing...' : 'Change Password'}
                  </button>
                </div>
              </div>
            ) : (
              <p className="text-gray-500 text-sm">
                Keep your account secure by using a strong password
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage; 